import { Building2, CreditCard, FileCheck, Globe, KeyRound, ShieldAlert, Store, Users } from 'lucide-react';

/*
 * Content for the Retail scenario walkthrough — Havenridge Retail Group.
 *
 * PROVENANCE, and it differs from the other packs: there is no `retail` demo
 * pack yet. Halcyon, Kestrel, Nordvik and Godwit take their artifact strings
 * verbatim from authored pack YAML. These are INVENTED, designed against the
 * seed specification at docs/industry-packs/retail-ORG-seed.md so that the
 * pack, once authored, matches this page rather than the other way round.
 *
 * When the pack lands, re-derive this file from the YAML and delete this note.
 */

export const havenridgeScenario = {
  org: {
    name: 'Havenridge Retail Group',
    subtitle: 'Five situations a retailer actually faces, from the store shelf to the shopper account.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: Store, text: '120 stores across 14 states' },
      { icon: Users, text: '~9,500 employees at peak' },
      { icon: Globe, text: 'Havenridge Direct e-commerce' },
      { icon: CreditCard, text: 'Private-label credit programme' },
    ],
  },
  scenarios: [
    {
      id: 'pos-compromise',
      number: 1,
      title: 'POS Malware Across the Store Fleet',
      tagline: 'Memory-scraping malware reaches terminals through a flat store network',
      icon: ShieldAlert,
      frameworks: ['PCI DSS v4.0', 'NIST CSF 2.0'],
      situation: {
        narrative: 'A district manager is phished, and the attacker pivots from a corporate workstation onto the '
          + 'store network over the SD-WAN. Memory-scraping malware lands on point-of-sale terminals at several '
          + 'stores and begins harvesting card data in the window between swipe and encryption. The store '
          + 'network turns out to be flat — no segmentation between corporate and card environments.',
        cast: [
          { name: 'Yara Mansour', role: 'Chief Information Security Officer' },
          { name: 'Anton Reyes', role: 'Director, Payments' },
          { name: 'Marisol Duarte', role: 'Regional Director, Southeast' },
          { name: 'Emeka Solanke', role: 'SOC Manager' },
        ],
      },
      timing: [
        { at: '−35 days', label: 'Malware deployed to terminals' },
        { at: '−28 days', label: 'Detected across multiple stores' },
        { at: '−25 days', label: 'Card-brand notification' },
        { at: '−7 days', label: 'Forensic report delivered' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Memory-scraping behaviour is detected on point-of-sale terminals',
          artifact: 'Memory-scraping malware on Tillstone POS terminal fleet',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Lateral movement from corporate into the store network is correlated',
          artifact: 'Lateral movement from corporate workstation onto store network',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a card-compromise investigation',
          artifact: 'POS Compromise Investigation — Multi-Store Card Exposure',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record scoping the exposure across affected stores',
          artifact: 'Cardholder Data Exposure via POS Terminal Compromise',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The flat store network is recorded as its own requirement gap',
          artifact: 'PCI DSS Req 1.2.1 — No Segmentation Between Corporate and Store Card Networks',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Card-brand notification is produced from the incident record',
          artifact: 'Card Brand Notification — POS Compromise',
        },
      ],
      enables: [
        'Store operations and security work one incident, so the district manager sees the same containment status as the SOC rather than waiting for a summary.',
        'The segmentation gap is a finding in its own right, which turns a cleanup into a funded network programme across 120 sites.',
      ],
      impact: {
        entities: [
          { name: 'Havenridge Stores', detail: 'Retail operations', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Retail Group', detail: 'Corporate IT', location: 'Marbury Falls, TN' },
        ],
        locations: [
          { name: 'Havenridge Flagship Store', detail: 'Retail store · terminals affected', location: 'Marbury Falls, TN' },
          { name: 'Brightwater Commons Store', detail: 'Retail store · terminals affected', location: 'Brightwater, GA' },
          { name: 'Cedarhurst Mall Store', detail: 'Retail store · terminals affected', location: 'Cedarhurst, OH' },
          { name: 'Havenridge Support Center', detail: 'Headquarters · initial compromise', location: 'Marbury Falls, TN' },
        ],
        applications: [
          { name: 'Tillstone POS terminal fleet', detail: 'Malware deployed', location: '120 stores' },
          { name: 'Store SD-WAN', detail: 'Flat — no segmentation', location: '120 stores' },
          { name: 'Beckworth payment gateway', location: 'Marbury Falls, TN' },
        ],
        vendors: [
          { name: 'Tillstone Commerce', detail: 'Point-of-sale platform', location: 'Minneapolis, MN' },
          { name: 'Beckworth Payment Processing', detail: 'Card acquiring and gateway', location: 'Wilmington, DE' },
          { name: 'Ravenwood Network Services', detail: 'Store SD-WAN', location: 'Dallas, TX' },
          { name: 'Tarnfield Endpoint Security', detail: 'Endpoint detection', location: 'Austin, TX' },
        ],
        data: [
          { name: 'Card Primary Account Number (PAN)', detail: '~4.2M card transactions/year', location: '120 stores' },
          { name: 'Card CVV / Expiration', detail: '~4.2M card transactions/year', location: '120 stores' },
          { name: 'Store Sales Transaction', detail: '~38M transactions/year', location: '120 stores' },
        ],
      },
    },
    {
      id: 'account-takeover',
      number: 2,
      title: 'E-Commerce Account Takeover',
      tagline: 'Credential stuffing against 1.6M shopper accounts',
      icon: KeyRound,
      frameworks: ['SOC 2', 'CCPA / CPRA', 'PCI DSS v4.0'],
      situation: {
        narrative: 'A credential-stuffing campaign runs against Havenridge Direct using credentials breached '
          + 'elsewhere. A fraction succeed, and those accounts are drained of loyalty points and used to place '
          + 'orders against stored cards. The volume is the problem: legitimate and malicious logins look alike '
          + 'until you can see the pattern across all of them.',
        cast: [
          { name: 'Joaquín Ferreira', role: 'Security Lead, Havenridge Direct' },
          { name: 'Priyanka Deshmukh', role: 'President, Havenridge Direct' },
          { name: 'Tabitha Lindgren', role: 'Security Analyst, Tier 2' },
        ],
      },
      timing: [
        { at: '−14 days', label: 'Campaign begins' },
        { at: '−12 days', label: 'Peak attempt volume' },
        { at: '−8 days', label: 'Controls tuned, MFA rollout begins' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Distributed login attempts are detected as a coordinated campaign',
          artifact: 'Credential-stuffing campaign against Havenridge Direct',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Successful takeovers are separated from failed attempts',
          artifact: 'Account takeover anomaly — loyalty and stored-card abuse',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering affected shopper accounts',
          artifact: 'E-Commerce Account Takeover — Credential Stuffing Campaign',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The authentication weakness is tracked against the SOC 2 criterion',
          artifact: 'SOC 2 CC6.1 — Authentication Controls Insufficient for Shopper Accounts',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Consumer notification is produced under state privacy law',
          artifact: 'Consumer Notification — Account Takeover',
        },
      ],
      enables: [
        'Havenridge Direct is its own entity, so the SOC 2 boundary covers e-commerce without pulling 120 stores into the examination.',
        'Loyalty abuse and card abuse are one incident, because the account that suffered both is one record.',
      ],
      impact: {
        entities: [
          { name: 'Havenridge Direct', detail: 'E-commerce — SOC 2 scope', location: 'Marbury Falls, TN' },
        ],
        locations: [
          { name: 'Havenridge Direct Technology Office', detail: 'Office · e-commerce engineering', location: 'Marbury Falls, TN' },
        ],
        applications: [
          { name: 'Marketwright Commerce Cloud', detail: 'Targeted platform', location: 'Marbury Falls, TN' },
          { name: 'Shopper account store', detail: 'Takeovers confirmed', location: 'Marbury Falls, TN' },
          { name: 'Beckworth payment gateway', detail: 'Stored-card abuse', location: 'Marbury Falls, TN' },
        ],
        vendors: [
          { name: 'Marketwright Commerce Cloud', detail: 'E-commerce platform', location: 'Seattle, WA' },
          { name: 'Wexmoor Security Analytics', detail: 'SIEM', location: 'Reston, VA' },
          { name: 'Fennimore Assurance Group', detail: 'SOC 2 assessor', location: 'Chicago, IL' },
        ],
        data: [
          { name: 'E-commerce Account Credential', detail: '~1.6M registered accounts', location: 'Marbury Falls, TN' },
          { name: 'Loyalty Member Record', detail: '~2.8M loyalty members', location: 'Marbury Falls, TN' },
          { name: 'Card Primary Account Number (PAN)', detail: 'Stored cards on file', location: 'Marbury Falls, TN' },
        ],
      },
    },
    {
      id: 'seasonal-access',
      number: 3,
      title: 'Seasonal Workforce Access',
      tagline: '2,700 seasonal hires, and the accounts that outlived them',
      icon: Users,
      frameworks: ['PCI DSS v4.0', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Havenridge hires roughly 2,700 seasonal workers for peak and releases them afterwards. An '
          + 'access review finds accounts still active months later — some at stores where the worker no longer '
          + 'appears in HR at all. Several carry point-of-sale and cash-handling permissions, which is a '
          + 'separation-of-duties problem as much as an access one.',
        cast: [
          { name: 'Idris Farouk', role: 'Identity & Access Engineer' },
          { name: 'Gordon Ashby', role: 'VP Store Operations' },
          { name: 'Curtis Nakamura', role: 'District Manager' },
        ],
      },
      timing: [
        { at: '−120 days', label: 'Seasonal onboarding wave' },
        { at: '−20 days', label: 'Offboarding gap found in review' },
        { at: 'Ongoing', label: 'Automated deprovisioning tracked' },
      ],
      response: [
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'A recertification campaign covers every seasonal account across 120 stores',
          artifact: 'Seasonal Workforce Access Recertification Campaign',
        },
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Cash-handling and POS permissions are checked against separation of duties',
          artifact: 'Store Cash-Handling Separation of Duties Matrix',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The offboarding failure is recorded as a finding, not a data-cleanup task',
          artifact: 'Seasonal Worker Accounts Not Deprovisioned After Separation',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'The deprovisioning backlog is worked as tracked operational load',
          artifact: 'Seasonal account deprovisioning backlog',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Automated deprovisioning is the remediation, with an owner and a date',
          artifact: 'Automated Deprovisioning from HR System of Record',
        },
      ],
      enables: [
        'Identity and the HR record reconcile against each other, which is the only way an account belonging to nobody gets noticed at all.',
        'A cash-handling permission and an access review are the same governance object, so a separation-of-duties conflict surfaces during recertification rather than during an audit.',
      ],
      impact: {
        entities: [
          { name: 'Havenridge Stores', detail: 'Retail operations', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Distribution', detail: 'Seasonal warehouse labour', location: 'Marbury Falls, TN' },
        ],
        locations: [
          { name: 'Havenridge Flagship Store', detail: 'Retail store', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Distribution Center — East', detail: 'Warehouse · seasonal labour', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Distribution Center — West', detail: 'Warehouse · seasonal labour', location: 'Parnell, NV' },
        ],
        applications: [
          { name: 'Kelbrook Identity SSO', detail: 'Orphaned accounts', location: 'Marbury Falls, TN' },
          { name: 'Tillstone POS terminal fleet', detail: 'Cash-handling permissions', location: '120 stores' },
          { name: 'Greenfield Workforce platform', detail: 'HR system of record', location: 'Marbury Falls, TN' },
        ],
        vendors: [
          { name: 'Kelbrook Identity Works', detail: 'SSO / IdP', location: 'Portland, OR' },
          { name: 'Greenfield Workforce', detail: 'Seasonal hiring and scheduling', location: 'Charlotte, NC' },
        ],
        data: [
          { name: 'Employee Social Security Number', detail: '~9,500 employee records', location: 'Marbury Falls, TN' },
          { name: 'Store Sales Transaction', detail: '~38M transactions/year', location: '120 stores' },
        ],
      },
    },
    {
      id: 'loyalty-vendor-breach',
      number: 4,
      title: 'Loyalty Vendor Breach',
      tagline: 'The marketing platform is breached, and consent state goes with it',
      icon: Building2,
      frameworks: ['CCPA / CPRA', 'GDPR', 'NIST CSF 2.0'],
      situation: {
        narrative: 'Loyalstone, the loyalty and marketing platform, discloses a breach exposing member records — '
          + 'including the marketing consent state that state privacy law requires be honoured. The exposure is '
          + 'not only personal data but the record of what each shopper agreed to, which is what makes the '
          + 'notification analysis harder than a simple contact-list leak.',
        cast: [
          { name: 'Beatrix Moreau', role: 'Third-Party Risk Analyst' },
          { name: 'Nathaniel Prosser', role: 'General Counsel & Chief Privacy Officer' },
        ],
      },
      timing: [
        { at: '−30 days', label: 'Vendor breach notification received' },
        { at: '−27 days', label: 'Due-diligence re-assessment opened' },
        { at: 'Ongoing', label: 'Encryption gap tracked as accepted risk' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'An out-of-cycle due-diligence package opens against the vendor',
          artifact: 'Loyalstone — Breach Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The security questionnaire is re-issued; encryption at rest comes back short',
          artifact: 'Loyalstone — Post-Breach Security Re-Assessment',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The gap becomes a logged, time-bound accepted risk with an owner',
          artifact: 'Accepted Risk — Loyalstone Encryption-at-Rest Gap Pending Remediation',
        },
        {
          module: 'Business',
          href: '/platform/business',
          action: 'The contract surfaces the notification and data-handling terms the vendor owed',
          artifact: 'Loyalstone master services agreement',
        },
        {
          module: 'Documents',
          href: '/platform/documents',
          action: 'Consumer notification is produced under state privacy law',
          artifact: 'CCPA Consumer Notification — Loyalty Data Exposure',
        },
      ],
      enables: [
        'Marketing consent state is classified data with an owner, so a breach analysis can say what each shopper had agreed to rather than approximating it.',
        'The contract sits beside the assessment, so a notification obligation is checkable at the moment it is tested rather than at renewal.',
      ],
      impact: {
        entities: [
          { name: 'Havenridge Retail Group', detail: 'Marketing and loyalty', location: 'Marbury Falls, TN' },
          { name: 'Loyalstone Customer Engagement', detail: 'Vendor — breached', location: 'Boston, MA' },
        ],
        locations: [
          { name: 'Havenridge Support Center', detail: 'Headquarters · marketing', location: 'Marbury Falls, TN' },
          { name: 'Loyalstone Headquarters', detail: 'Vendor site — breached', location: 'Boston, MA' },
        ],
        applications: [
          { name: 'Loyalstone Customer Engagement', detail: 'Breached platform', location: 'Boston, MA' },
          { name: 'Marketwright Commerce Cloud', detail: 'Loyalty integration', location: 'Marbury Falls, TN' },
        ],
        vendors: [
          { name: 'Loyalstone Customer Engagement', detail: 'Breached — loyalty and marketing', location: 'Boston, MA' },
          { name: 'Beckworth Payment Processing', detail: 'Routine annual review', location: 'Wilmington, DE' },
        ],
        data: [
          { name: 'Loyalty Member Record', detail: '~2.8M loyalty members', location: 'Boston, MA' },
          { name: 'Marketing Consent State', detail: '~2.8M loyalty members', location: 'Boston, MA' },
          { name: 'Customer Home Address', detail: '~2.8M loyalty members', location: 'Boston, MA' },
          { name: 'Loyalty platform sync', detail: 'Transmission', location: 'Marbury Falls, TN → Boston, MA' },
        ],
      },
    },
    {
      id: 'pci-programme',
      number: 5,
      title: 'PCI DSS v4.0 Programme',
      tagline: 'Card-present, card-not-present, and private-label under one programme',
      icon: FileCheck,
      frameworks: ['PCI DSS v4.0', 'GLBA', 'SOC 2'],
      situation: {
        narrative: 'Three card environments sit under one programme: card-present across 120 stores, '
          + 'card-not-present at Havenridge Direct, and the private-label credit programme that brings GLBA '
          + 'obligations with it. Two requirement gaps are open and a QSA assessment is scheduled.',
        cast: [
          { name: 'Simone Achterberg', role: 'PCI Compliance Manager' },
          { name: 'Rowan Castellanos', role: 'GRC Analyst' },
          { name: 'Anton Reyes', role: 'Director, Payments' },
        ],
      },
      timing: [
        { at: '−180 days', label: 'Programme activated' },
        { at: 'In flight', label: 'Two requirement gaps open' },
        { at: '+45 days', label: 'QSA on-site assessment' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Programme activation tracks control instances across all three card scopes',
          artifact: 'PCI DSS v4.0 Compliance Programme',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Open remediation finding against cardholder data at rest',
          artifact: 'PCI DSS Req 3.5.1 — Cardholder Data at Rest Not Rendered Unreadable',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Open remediation finding against administrative access to the card environment',
          artifact: 'PCI DSS Req 8.4.2 — MFA Not Enforced for All CDE Admin Access',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Segmentation evidence attaches to the requirement it satisfies',
          artifact: 'Store Network Segmentation Penetration Test Summary',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'A certification project carries the engagement to the assessment window',
          artifact: 'PCI DSS v4.0 QSA Certification — Havenridge Retail Group',
        },
      ],
      enables: [
        'Three card environments answer to one programme, so a control implemented once is evidenced once rather than three times.',
        'The private-label programme brings GLBA into the same control taxonomy as PCI, which is what stops two compliance teams from building parallel evidence.',
      ],
      impact: {
        entities: [
          { name: 'Havenridge Retail Group', detail: 'All card environments', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Card Services', detail: 'Private-label credit — GLBA scope', location: 'Marbury Falls, TN' },
        ],
        locations: [
          { name: 'Havenridge Support Center', detail: 'Headquarters · payments', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Direct Technology Office', detail: 'Office · card-not-present', location: 'Marbury Falls, TN' },
          { name: 'Havenridge Flagship Store', detail: 'Retail store · card-present', location: 'Marbury Falls, TN' },
        ],
        applications: [
          { name: 'Tillstone POS terminal fleet', detail: 'Card-present scope', location: '120 stores' },
          { name: 'Marketwright Commerce Cloud', detail: 'Card-not-present scope', location: 'Marbury Falls, TN' },
          { name: 'Hallmoor private-label platform', detail: 'GLBA scope', location: 'Marbury Falls, TN' },
        ],
        vendors: [
          { name: 'Fennimore Assurance Group', detail: 'PCI QSA and SOC 2 assessor', location: 'Chicago, IL' },
          { name: 'Hallmoor Card Services', detail: 'Private-label credit issuer', location: 'Sioux Falls, SD' },
          { name: 'Beckworth Payment Processing', detail: 'Card acquiring', location: 'Wilmington, DE' },
        ],
        data: [
          { name: 'Card Primary Account Number (PAN)', detail: '~4.2M card transactions/year', location: 'Marbury Falls, TN' },
          { name: 'Private-Label Credit Application', detail: '~86,000 applications/year', location: 'Marbury Falls, TN' },
          { name: 'Card CVV / Expiration', detail: '~4.2M card transactions/year', location: 'Marbury Falls, TN' },
        ],
      },
    },
  ],
};
